import { useEffect, useId, useRef, useState } from 'react';
import { DOCTOR_SEARCH_MIN, useDoctorSearch } from '@/application/usecases/useClients';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import styles from './DoctorPicker.module.css';

/**
 * The doctor a picker holds: the record's id, the ID a person reads it by
 * (`DR-0012`), the name and the clinic, which are what tells one doctor from
 * another of the same name, and how many cases the doctor has, for a sheet that
 * has to say what choosing them does.
 */
export interface PickedDoctor {
  id: string;
  code: string;
  name: string;
  clinic: string | null;
  caseCount: number;
}

export interface DoctorPickerProps {
  /** The doctor chosen, or null while the person is still searching. */
  value: PickedDoctor | null;
  /** A doctor picked from the matches, or null when Change takes the choice back. */
  onChange: (next: PickedDoctor | null) => void;
  /** What the field is called, above the search box and above the doctor chosen. */
  label: string;
  /** A refusal to show under the field, the server's or the form's own. */
  error?: string | null;
  /** The id of a doctor who is never offered: the one a merge is into, who cannot be merged with itself. */
  exclude?: string | null;
  /** Focus goes to the search box when the sheet it is in opens. */
  autoFocus?: boolean;
}

/**
 * Choosing one doctor from the records. The request of 6 October 2026 (second),
 * item 4, and of 6 October 2026 (third), items 1 and 2: the doctor to merge
 * into another is searched for, not typed, and is told from its namesakes by
 * its ID.
 *
 * Type a name, a clinic or an ID, and the doctors that match are listed under
 * the field, a few at a time and by name, each with its ID, its clinic and how
 * many cases it has; press one and it is held, with Change beside it to search
 * again. It only ever answers with a record that exists: a name that matches
 * nobody is not a doctor here.
 *
 * Controlled, and it keeps nothing but what is typed in the search box: the
 * form holds the choice. One column at every width, every row a touch target.
 */
export function DoctorPicker({
  value,
  onChange,
  label,
  error = null,
  exclude = null,
  autoFocus = false,
}: DoctorPickerProps) {
  const ids = { input: useId(), label: useId(), hint: useId(), error: useId() };
  const [text, setText] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const change = useRef<HTMLButtonElement>(null);
  // Picking takes the pressed row away, and Change the button: focus follows to what took its place.
  const moveFocus = useRef(false);

  // Nothing is asked for while a doctor is held.
  const search = useDoctorSearch(value === null ? text : '');
  const typed = text.trim();
  const matches = (search.data?.items ?? []).filter((doctor) => doctor.id !== exclude);

  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    (value === null ? input.current : change.current)?.focus();
  }, [value]);

  function choose(next: PickedDoctor | null) {
    moveFocus.current = true;
    onChange(next);
  }

  if (value !== null) {
    return (
      <div className={styles.picker}>
        <span id={ids.label} className={styles.l}>
          {label}
        </span>
        <div className={styles.picked} role="group" aria-labelledby={ids.label}>
          <span className={styles.who}>
            <b>{value.name}</b>
            <span>
              <span className={styles.code}>{value.code}</span>
              {value.clinic !== null && ` · ${value.clinic}`}
            </span>
          </span>
          <Button ref={change} variant="text" onClick={() => choose(null)}>
            {strings.clients.pickerChange}
          </Button>
        </div>
        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.picker}>
      <label className={styles.l} htmlFor={ids.input}>
        {label}
      </label>
      <input
        ref={input}
        id={ids.input}
        className={styles.input}
        value={text}
        autoComplete="off"
        spellCheck={false}
        placeholder={strings.clients.pickerPlaceholder}
        aria-invalid={error !== null || undefined}
        aria-describedby={error !== null ? `${ids.error} ${ids.hint}` : ids.hint}
        data-autofocus={autoFocus || undefined}
        onChange={(event) => setText(event.target.value)}
      />

      {error !== null && (
        <p id={ids.error} className={styles.error} role="alert">
          {error}
        </p>
      )}

      {/* One line under the field says where the search stands, and is read out as it changes. */}
      <p id={ids.hint} className={styles.hint} role="status">
        {typed.length < DOCTOR_SEARCH_MIN
          ? strings.clients.pickerHint
          : search.isError
            ? strings.errors.server
            : search.data === undefined
              ? strings.clients.pickerSearching
              : matches.length === 0
                ? strings.clients.pickerNone
                : ''}
      </p>

      {matches.length > 0 && (
        <ul className={styles.results} aria-label={strings.clients.pickerResults}>
          {matches.map((doctor) => (
            <li key={doctor.id}>
              <button
                type="button"
                className={styles.result}
                onClick={() =>
                  choose({
                    id: doctor.id,
                    code: doctor.code,
                    name: doctor.name,
                    clinic: doctor.clinic,
                    caseCount: doctor.caseCount,
                  })
                }
              >
                <b>{doctor.name}</b>
                {/* The ID first: it is what tells two doctors of one name apart. */}
                <span>
                  <span className={styles.code}>{doctor.code}</span>
                  {doctor.clinic !== null && ` · ${doctor.clinic}`}
                  {` · ${strings.clients.casesCounted(doctor.caseCount)}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
